import { PropRuntimeGraph } from '../../../shared/types/PropRuntimeGraph';
import { PropsMechanicsEngine } from './PropsMechanicsEngine';
import { PropEventBus } from './propMechanics/PropEventBus';
import { normalizeMagicDelta } from './propMechanics/PropMagicUtils';
import {
    BattleRuntimeSnapshot,
    BattleRuntimeSnapshotItem,
    BattleRuntimeState,
    defaultPropRandomSource,
    PropRandomSource,
    PropRuntimeEvent,
    PropRuntimeEventReason,
    RuntimePropInstance,
} from './propMechanics/PropMechanicsTypes';
import {
    getRuntimeYield,
    isEmptyPropInstance,
    pickRandomItems,
    uniqueLiveItems,
} from './propMechanics/runtimeNodes/RuntimeNodeUtils';

export interface BattleManagerOptions {
    /** 盘面行数，默认 4。 */
    rows?: number;
    /** 盘面列数，默认 5。 */
    cols?: number;
    /** 对外暴露的首行坐标，默认 1。 */
    firstRowIndex?: number;
    /** 对外暴露的首列坐标，默认 0。 */
    firstColumnIndex?: number;
    /** 初始原料原型。BattleManager 会为这些原型创建材料库实例。 */
    initialMaterials?: PropRuntimeGraph[];
    /** 已获得的配方原型。配方不上场，但会持续监听回合事件。 */
    initialRecipes?: PropRuntimeGraph[];
    /** 已获得的辅料原型。辅料通常由主动使用事件触发。 */
    initialConsumables?: PropRuntimeGraph[];
    /** 未显式传入初始材料时，从目录随机创建的初始原料数量，默认 5。 */
    initialMaterialCount?: number;
    /** 随机源。传入可复现随机源后，战斗流程可以稳定回放。 */
    rng?: PropRandomSource;
}

/**
 * 炼金战斗流程管理器。
 *
 * 该类只管理“游戏流程”：
 * - 创建和维护运行时状态；
 * - 每轮开始前从材料库抽取道具填充盘面；
 * - 在关键时机派发事件；
 * - 结算盘面基础魔力；
 * - 对外提供入手、移除、使用、消除等流程接口。
 *
 * 道具效果本身不写在 BattleManager 中，而是由 PropsMechanicsEngine 监听事件后解释 PropRuntimeGraph。
 * 这样可以保持流程层和道具逻辑层解耦，后续替换 UI、动画或网络同步方式时，不需要改节点实现。
 */
export class BattleManager {
    private static instance?: BattleManager;

    /**
     * 获取全局唯一的战斗流程管理器实例。
     *
     * 首次调用会使用传入的道具目录和初始化选项创建实例；后续调用会直接返回已有实例，
     * 不会重新应用新的 `catalog` 或 `options`，避免运行中的战斗状态被隐式覆盖。
     *
     * @param catalog 完整道具运行时原型目录，仅在首次创建实例时使用。
     * @param options 可选初始化参数，仅在首次创建实例时使用。
     * @returns 当前全局唯一的 BattleManager 实例。
     */
    public static getInstance(catalog: PropRuntimeGraph[], options: BattleManagerOptions = {}): BattleManager {
        if (!BattleManager.instance) BattleManager.instance = new BattleManager(catalog, options);
        return BattleManager.instance;
    }

    /**
     * 判断当前是否已经存在战斗流程管理器实例。
     *
     * @returns 已创建且尚未释放实例时返回 true。
     */
    public static hasInstance(): boolean {
        return Boolean(BattleManager.instance);
    }

    /**
     * 释放当前战斗流程管理器单例。
     *
     * 如果实例存在，会先调用 `endGame()` 注销道具机制监听和战斗事件，再清空静态引用。
     * 场景销毁或需要重新开始一局完整战斗时，应先调用该方法，再通过 `getInstance()` 创建新实例。
     */
    public static disposeInstance(): void {
        if (!BattleManager.instance) return;
        BattleManager.instance.endGame();
        BattleManager.instance = undefined;
    }

    private readonly rng: PropRandomSource;
    private readonly eventBus = new PropEventBus();
    private readonly state: BattleRuntimeState;
    private readonly mechanicsEngine: PropsMechanicsEngine;
    private readonly initialMaterials?: PropRuntimeGraph[];
    private readonly initialRecipes?: PropRuntimeGraph[];
    private readonly initialConsumables?: PropRuntimeGraph[];
    private readonly initialMaterialCount: number;
    private gameInitialized = false;
    private gameEnded = false;

    /**
     * 创建一局炼金战斗流程管理器。
     *
     * @param catalog 完整道具运行时原型目录。生成、转化、原型筛选和“空位置”占位都会从该目录查找原型；
     * 目录中必须包含名称为“空位置”的原型，否则构造过程会抛出错误，避免盘面出现 null 或伪空格。
     * @param options 可选初始化参数，包含盘面尺寸、初始原料、初始配方、初始辅料和随机源。
     *
     * 构造流程：
     * 1. 初始化随机源；未传入 `options.rng` 时使用默认随机源。
     * 2. 根据目录、盘面尺寸和初始配置创建纯数据战斗状态。
     * 3. 创建 `PropsMechanicsEngine`，并挂载到同一个事件总线上。
     * 4. 校验数据库中存在“空位置”原型。
     * 5. 缓存初始材料、配方、辅料配置；真正的实例化发生在 `startGame()`。
     * 6. 每轮开始前会重新抽取材料库实例进入盘面，并用“空位置”补齐空格。
     *
     * 设计原因：
     * - BattleManager 只负责战斗流程和状态推进，不直接实现道具效果。
     * - 道具效果由 `PropsMechanicsEngine` 监听事件后解释 `PropRuntimeGraph` 执行。
     * - 空格使用数据库“空位置”原型实例占位，可以让目标筛选、位置判断和表现同步使用统一实例模型。
     *
     * 边界情况：
     * - 缺少“空位置”原型会立即抛错，阻止战斗在不完整盘面状态下运行。
     * - 初始材料、配方和辅料在 `startGame()` 时创建实例，节点运行时也从此时开始监听事件。
     * - 构造函数只完成基础状态搭建；正式开始游戏应由调用方显式调用 `startGame()`。
     */
    private constructor(catalog: PropRuntimeGraph[], options: BattleManagerOptions = {}) {
        this.rng = options.rng ?? defaultPropRandomSource;
        this.state = this.createInitialState(catalog, options);
        this.mechanicsEngine = new PropsMechanicsEngine(this.state, this.eventBus, this.rng);
        this.mechanicsEngine.attach();
        this.ensureEmptyPropExists();
        this.initialMaterials = options.initialMaterials;
        this.initialRecipes = options.initialRecipes;
        this.initialConsumables = options.initialConsumables;
        this.initialMaterialCount = options.initialMaterialCount ?? 5;
    }

    /** 当前战斗状态。调用方可以只读使用；修改状态应优先通过 BattleManager 方法完成。 */
    public getState(): BattleRuntimeState {
        return this.state;
    }

    /** 道具机制引擎。主要用于调试或单元测试直接派发事件。 */
    public getMechanicsEngine(): PropsMechanicsEngine {
        return this.mechanicsEngine;
    }

    /** 事件总线。表现层可订阅事件播放动画，逻辑层也通过它解耦。 */
    public getEventBus(): PropEventBus {
        return this.eventBus;
    }

    /**
     * 保存当前运行时快照。
     *
     * BattleManager 不负责持久化；调用方应把返回值写入本地存档或服务端。快照会保留当前存活道具，
     * 以及被永久加成规则引用的已移除来源道具，避免恢复后消耗型辅料产生的持续效果丢失。
     */
    public saveSnapshot(): BattleRuntimeSnapshot {
        const persistentSourceIds = new Set(this.state.permanentBoostRules.map((rule) => rule.sourceId));
        const items = this.state.items
            .filter((item) => !isEmptyPropInstance(item))
            .filter((item) => item.alive || persistentSourceIds.has(item.id))
            .map((item) => this.serializeSnapshotItem(item));
        return {
            version: 1,
            savedAt: Date.now(),
            rows: this.state.rows,
            cols: this.state.cols,
            firstRowIndex: this.state.firstRowIndex,
            firstColumnIndex: this.state.firstColumnIndex,
            commissionIndex: this.state.commissionIndex,
            roundIndex: this.state.roundIndex,
            totalRoundIndex: this.state.totalRoundIndex,
            extraCommissionRounds: this.state.extraCommissionRounds,
            currentRoundMagic: this.state.currentRoundMagic,
            totalMagic: this.state.totalMagic,
            commissionOnceKeys: Array.from(this.state.commissionOnceKeys),
            materialLibrary: [...this.state.materialLibrary],
            recipes: [...this.state.recipes],
            consumables: [...this.state.consumables],
            boardItemIds: this.mechanicsEngine.getAllBoardItems(false).map((item) => item.id),
            items,
            permanentBoostRules: cloneJson(this.state.permanentBoostRules),
        };
    }

    /**
     * 从快照恢复运行时状态。
     *
     * 该方法只接收外部传入的快照数据，不读取或写入持久化存储。恢复时会先派发旧实例的离场和移除事件，
     * 再重建实例、节点运行时、持有列表、盘面和计时器显示。
     */
    public restoreSnapshot(snapshot: BattleRuntimeSnapshot): void {
        this.clearRuntimeBeforeRestore();
        const restoredItems = this.restoreSnapshotItems(snapshot);
        this.restoreSnapshotState(snapshot, restoredItems);
        this.rebuildRestoredNodeRuntimes(snapshot);
        this.emitRestoredOwnerEvents();
        this.state.permanentBoostRules = cloneJson(snapshot.permanentBoostRules);
        this.emit({ type: 'permanentBoostRulesChanged', reason: 'snapshotRestore' });
        this.restoreSnapshotBoard(snapshot);
        this.emitRestoredTimers('snapshotRestored');
        this.gameInitialized = true;
        this.gameEnded = false;
    }

    /** 开始一局游戏。 */
    public startGame(): void {
        if (this.gameEnded || this.gameInitialized) return;
        this.emit({ type: 'gameStarted', reason: 'gameStarted' });
        this.initializeGameRuntime();
    }

    /** 结束当前游戏，注销引擎监听，避免场景切换后仍收到事件。 */
    public endGame(): void {
        if (this.gameEnded) return;
        this.emit({ type: 'gameEnded', reason: 'gameEnded' });
        this.mechanicsEngine.releaseAll();
        this.eventBus.clearScope('round');
        this.gameEnded = true;
    }

    /** 开始一个委托。每个委托开始时清理“每委托限一次”的标记。 */
    public startCommission(commissionIndex: number): void {
        this.state.commissionIndex = commissionIndex;
        this.state.roundIndex = 0;
        this.state.extraCommissionRounds = 0;
        this.state.commissionOnceKeys.clear();
        this.emit({ type: 'commissionStarted', commissionIndex, reason: 'commissionStarted' });
        this.drawBoardFromMaterialLibrary('commissionStarted');
    }

    /** 结束当前委托。 */
    public endCommission(): void {
        this.emit({ type: 'commissionEnded', commissionIndex: this.state.commissionIndex, reason: 'commissionEnded' });
    }

    /**
     * 执行一轮调和。
     *
     * 标准顺序：
     * 1. 全清上一轮盘面，从材料库随机抽取真实原料进入盘面，并用“空位置”补齐空格；
     * 2. 派发一次全局 roundStarted，供表现层和流程面板观察；
     * 3. 按配方、辅料、盘面原料顺序逐个触发道具的回合开始入口，且每个道具先结算基础魔力；
     * 4. 应用持续性全局效果并结算本轮状态；
     * 5. 派发 roundEnded 并清理回合临时产出；
     * 6. 轮结束后不立刻洗盘，直到下一次调和开始前再准备新盘面。
     */
    public runRound(): number {
        this.state.roundIndex += 1;
        this.state.totalRoundIndex += 1;
        this.state.currentRoundMagic = 0;
        this.drawBoardFromMaterialLibrary('roundStarted');

        this.emit({
            type: 'roundStarted',
            commissionIndex: this.state.commissionIndex,
            roundIndex: this.state.roundIndex,
            totalRoundIndex: this.state.totalRoundIndex,
            reason: 'roundStarted',
        });
        this.mechanicsEngine.prepareRoundYieldBoosts();
        this.triggerPropTurnStartInOrder();
        this.emit({
            type: 'roundEnded',
            commissionIndex: this.state.commissionIndex,
            roundIndex: this.state.roundIndex,
            totalRoundIndex: this.state.totalRoundIndex,
            reason: 'roundEnded',
        });
        this.clearRoundTemporaryState();
        return this.state.currentRoundMagic;
    }

    /** 获得一个道具原型，并创建对应运行时实例。 */
    public acquireProp(graph: PropRuntimeGraph): RuntimePropInstance {
        return this.mechanicsEngine.acquireProp(graph);
    }

    /** 从玩家持有列表中移除一个实例。 */
    public removeProp(item: RuntimePropInstance): void {
        const removedRow = item.row;
        const removedCol = item.col;
        item.alive = false;
        this.removeFromOwnerLists(item.id);
        if (item.row >= 0 && item.col >= 0) this.mechanicsEngine.removeFromBoard(item);
        this.emit({ type: 'propRemoved', item, target: item, row: removedRow, col: removedCol, reason: 'removeProp' });
        this.mechanicsEngine.disposeNodeRuntime(item);
    }

    /**
     * 主动使用辅料。
     *
     * 使用事件先触发节点逻辑，随后默认消耗该辅料实例。
     * 如果后续存在“不消耗辅料”的设计，可以通过 item.meta 或新增参数扩展。
     */
    public useProp(item: RuntimePropInstance): void {
        if (!item.alive || isEmptyPropInstance(item)) return;
        item.useCount += 1;
        this.emit({ type: 'propUsed', item, reason: 'useProp' });
        if (item.graph.type === 2) this.removeProp(item);
    }

    /**
     * 外部流程消除一个道具。
     *
     * 若目标有保护层，则消耗保护但目标不会死亡；消除动作仍视为成功。
     */
    public eliminateProp(item: RuntimePropInstance, source?: RuntimePropInstance): boolean {
        if (!item.alive || isEmptyPropInstance(item)) return false;
        if (item.protectStacks > 0) {
            return true;
        }
        const removedRow = item.row;
        const removedCol = item.col;
        item.alive = false;
        this.removeFromOwnerLists(item.id);
        if (item.row >= 0 && item.col >= 0) this.mechanicsEngine.removeFromBoard(item);
        this.emit({ type: 'propRemoved', item, target: item, source, row: removedRow, col: removedCol, reason: 'eliminateProp' });
        this.emit({ type: 'propEliminated', item, target: item, source, row: removedRow, col: removedCol, reason: 'eliminateProp' });
        this.mechanicsEngine.disposeNodeRuntime(item);
        return true;
    }

    /** 把一个实例放到盘面指定位置。 */
    public placeItem(item: RuntimePropInstance, row: number, col: number): void {
        this.mechanicsEngine.placeItem(item, row, col);
    }

    private serializeSnapshotItem(item: RuntimePropInstance): BattleRuntimeSnapshotItem {
        return {
            id: item.id,
            prototypeId: item.graph._id,
            alive: item.alive,
            row: item.row,
            col: item.col,
            grow: item.grow,
            triggerCount: item.triggerCount,
            useCount: item.useCount,
            protectStacks: item.protectStacks,
            purified: item.purified,
            collectedMagic: item.collectedMagic,
            meta: cloneJson(item.meta),
            negativeEffects: [...item.negativeEffects],
            nodeStates: this.serializeNodeStates(item),
        };
    }

    private serializeNodeStates(item: RuntimePropInstance): Record<number, unknown> {
        const states: Record<number, unknown> = {};
        item.nodeRuntime?.nodes.forEach((node, nodeId) => {
            if (typeof node.serializeState !== 'function') return;
            states[nodeId] = cloneJson(node.serializeState());
        });
        return states;
    }

    private clearRuntimeBeforeRestore(): void {
        const removedItems = uniqueLiveItems([
            ...this.readAliveItems(this.state.materialLibrary),
            ...this.readAliveItems(this.state.recipes),
            ...this.readAliveItems(this.state.consumables),
        ]);
        const removedPositions = new Map<string, { row: number; col: number }>();
        for (const item of this.mechanicsEngine.getAllBoardItems(true)) {
            removedPositions.set(item.id, { row: item.row, col: item.col });
        }
        this.mechanicsEngine.clearBoard(true);
        for (const item of removedItems) {
            const position = removedPositions.get(item.id);
            this.emit({
                type: 'propRemoved',
                item,
                target: item,
                row: position?.row ?? item.row,
                col: position?.col ?? item.col,
                reason: 'snapshotRestore',
            });
        }
        this.mechanicsEngine.releaseAll();
        this.eventBus.clearScope('round');
    }

    private restoreSnapshotItems(snapshot: BattleRuntimeSnapshot): RuntimePropInstance[] {
        const restoredItems: RuntimePropInstance[] = [];
        for (const snapshotItem of snapshot.items) {
            const item = this.createRestoredItem(snapshotItem);
            if (!item) continue;
            restoredItems.push(item);
            this.state.items.push(item);
        }
        return restoredItems;
    }

    private createRestoredItem(snapshotItem: BattleRuntimeSnapshotItem): RuntimePropInstance | undefined {
        const graph = this.state.catalog.find((item) => item._id === snapshotItem.prototypeId);
        if (!graph) {
            console.warn(`快照恢复失败：找不到道具原型 ${snapshotItem.prototypeId}`);
            return undefined;
        }
        const item: RuntimePropInstance = {
            id: snapshotItem.id,
            graph,
            row: -1,
            col: -1,
            alive: snapshotItem.alive,
            grow: Number(snapshotItem.grow) || 0,
            triggerCount: Math.max(0, Math.floor(Number(snapshotItem.triggerCount) || 0)),
            useCount: Math.max(0, Math.floor(Number(snapshotItem.useCount) || 0)),
            protectStacks: Math.max(0, Math.floor(Number(snapshotItem.protectStacks) || 0)),
            collectedMagic: Number(snapshotItem.collectedMagic) || 0,
            meta: cloneJson(snapshotItem.meta ?? {}),
            negativeEffects: Array.isArray(snapshotItem.negativeEffects) ? [...snapshotItem.negativeEffects] : [],
        };
        if (snapshotItem.purified) item.purified = true;
        return item;
    }

    private restoreSnapshotState(snapshot: BattleRuntimeSnapshot, restoredItems: RuntimePropInstance[]): void {
        const restoredIds = new Set(restoredItems.map((item) => item.id));
        const aliveIds = new Set(restoredItems.filter((item) => item.alive).map((item) => item.id));
        this.state.rows = snapshot.rows;
        this.state.cols = snapshot.cols;
        this.state.firstRowIndex = snapshot.firstRowIndex;
        this.state.firstColumnIndex = snapshot.firstColumnIndex;
        this.state.board = Array.from({ length: snapshot.rows }, () => Array.from({ length: snapshot.cols }, () => ''));
        this.state.commissionIndex = snapshot.commissionIndex;
        this.state.roundIndex = snapshot.roundIndex;
        this.state.totalRoundIndex = snapshot.totalRoundIndex;
        this.state.extraCommissionRounds = snapshot.extraCommissionRounds;
        this.state.currentRoundMagic = normalizeMagicDelta(snapshot.currentRoundMagic);
        this.state.totalMagic = normalizeMagicDelta(snapshot.totalMagic);
        this.state.commissionOnceKeys = new Set(snapshot.commissionOnceKeys);
        this.state.materialLibrary = snapshot.materialLibrary.filter((id) => aliveIds.has(id));
        this.state.recipes = snapshot.recipes.filter((id) => aliveIds.has(id));
        this.state.consumables = snapshot.consumables.filter((id) => aliveIds.has(id));
        this.state.rarityWeightRules = [];
        this.state.rarityWeightMultipliers = {};
        this.state.permanentBoostRules = [];
        this.state.roundYieldBoosts = {};
        for (const id of snapshot.boardItemIds) {
            if (!restoredIds.has(id)) console.warn(`快照恢复提示：盘面实例 ${id} 不存在，已跳过落位`);
        }
    }

    private emitRestoredOwnerEvents(): void {
        for (const item of [
            ...this.readAliveItems(this.state.materialLibrary),
            ...this.readAliveItems(this.state.recipes),
            ...this.readAliveItems(this.state.consumables),
        ]) {
            this.emit({ type: 'propAcquired', item, prototype: item.graph, prototypeId: item.graph._id, reason: 'snapshotRestore' });
        }
    }

    private rebuildRestoredNodeRuntimes(snapshot: BattleRuntimeSnapshot): void {
        const nodeStateMap = new Map(snapshot.items.map((item) => [item.id, item.nodeStates]));
        for (const item of this.state.items) {
            if (!item.alive) continue;
            this.mechanicsEngine.rebuildNodeRuntime(item);
            this.restoreNodeStates(item, nodeStateMap.get(item.id) ?? {});
        }
    }

    private restoreNodeStates(item: RuntimePropInstance, states: Record<number, unknown>): void {
        if (!item.nodeRuntime) return;
        for (const [rawNodeId, state] of Object.entries(states)) {
            const nodeId = Number(rawNodeId);
            const node = item.nodeRuntime.nodes.get(nodeId);
            if (!node || typeof node.restoreState !== 'function') continue;
            node.restoreState(cloneJson(state));
        }
    }

    private restoreSnapshotBoard(snapshot: BattleRuntimeSnapshot): void {
        this.mechanicsEngine.fillEmptyBoardSlots();
        const ids = [...snapshot.boardItemIds];
        const materialIds = new Set(this.state.materialLibrary);
        const candidates = pickRandomItems(
            ids
                .map((id) => this.state.items.find((item) => item.id === id))
                .filter((item): item is RuntimePropInstance => Boolean(item?.alive && item.graph.type === 1 && !isEmptyPropInstance(item)))
                .filter((item) => {
                    if (materialIds.has(item.id)) return true;
                    console.warn(`[${item.graph.name || item.graph._id}] skipped board restore because it is not in materialLibrary`);
                    return false;
                }),
            ids.length,
            this.rng,
        );
        for (const item of candidates) this.mechanicsEngine.placeItemToRandomEmptySlot(item, 'snapshotRestore');
    }

    private emitRestoredTimers(reason: PropRuntimeEventReason): void {
        for (const item of this.state.items) {
            if (!item.alive || !item.nodeRuntime) continue;
            item.nodeRuntime.nodes.forEach((node) => {
                const emitter = node as unknown as { emitCurrentTimer?: (reason: PropRuntimeEventReason) => void };
                if (typeof emitter.emitCurrentTimer !== 'function') return;
                emitter.emitCurrentTimer(reason);
            });
        }
    }

    /**
     * 在 `gameStarted` 后完成一次性运行时初始化。
     *
     * 材料库优先使用调用方传入的 `initialMaterials`；未传入时从目录中随机创建默认数量的
     * 非衍生原料实例。配方和辅料也在这里实例化，使其事件节点可以在游戏开始后参与监听。
     */
    private initializeGameRuntime(): void {
        if (this.gameInitialized) return;
        this.mechanicsEngine.clearBoard(true);
        const materialGraphs = this.resolveInitialMaterialGraphs();
        for (const graph of materialGraphs) this.acquireProp(graph);
        for (const graph of this.initialRecipes ?? []) this.acquireProp(graph);
        for (const graph of this.initialConsumables ?? []) this.acquireProp(graph);
        this.mechanicsEngine.fillEmptyBoardSlots();
        this.gameInitialized = true;
    }

    /** 解析开局材料原型；自动随机时排除衍生物和“空位置”。 */
    private resolveInitialMaterialGraphs(): PropRuntimeGraph[] {
        if (this.initialMaterials?.length) return this.initialMaterials;
        const candidates = this.state.catalog.filter((graph) => graph.type === 1 && graph.rarity === 1 && !graph.derived && graph.name !== '空位置');
        const result: PropRuntimeGraph[] = [];
        for (let index = 0; index < this.initialMaterialCount && candidates.length > 0; index++) {
            result.push(candidates[this.rng.int(0, candidates.length)]);
        }
        return result;
    }

    private createInitialState(catalog: PropRuntimeGraph[], options: BattleManagerOptions): BattleRuntimeState {
        const rows = options.rows ?? 4;
        const cols = options.cols ?? 5;
        const firstRowIndex = options.firstRowIndex ?? 1;
        const firstColumnIndex = options.firstColumnIndex ?? 0;
        return {
            rows,
            cols,
            firstRowIndex,
            firstColumnIndex,
            items: [],
            catalog,
            materialLibrary: [],
            recipes: [],
            consumables: [],
            board: Array.from({ length: rows }, () => Array.from({ length: cols }, () => '')),
            commissionIndex: 0,
            roundIndex: 0,
            totalRoundIndex: 0,
            extraCommissionRounds: 0,
            commissionOnceKeys: new Set<string>(),
            currentRoundMagic: 0,
            totalMagic: 0,
            rarityWeightMultipliers: {},
            rarityWeightRules: [],
            permanentBoostRules: [],
            roundYieldBoosts: {},
        };
    }

    private ensureEmptyPropExists(): void {
        if (!this.state.catalog.some((item) => item.name === '空位置')) {
            throw new Error('战斗初始化失败：道具目录中缺少名称为“空位置”的原型');
        }
    }

    private drawBoardFromMaterialLibrary(reason: PropRuntimeEventReason): void {
        this.mechanicsEngine.clearBoard(true);
        const maxCount = this.state.rows * this.state.cols;
        const candidates = uniqueLiveItems(this.state.materialLibrary
            .map((id) => this.state.items.find((item) => item.id === id))
            .filter(Boolean) as RuntimePropInstance[])
            .filter((item) => item.graph.type === 1 && !isEmptyPropInstance(item));
        const selected = pickRandomItems(candidates, Math.min(maxCount, candidates.length), this.rng);
        const positions = pickRandomItems(this.createBoardPositions(), selected.length, this.rng);
        this.mechanicsEngine.beginEventBatch();
        try {
            for (let i = 0; i < selected.length; i++) {
                const { row, col } = positions[i];
                this.mechanicsEngine.placeItem(selected[i], row, col, reason);
            }
        } finally {
            this.mechanicsEngine.endEventBatch();
        }
        this.mechanicsEngine.fillEmptyBoardSlots();
        this.mechanicsEngine.prepareRoundProtection();
    }

    private createBoardPositions(): Array<{ row: number; col: number }> {
        const positions: Array<{ row: number; col: number }> = [];
        const lastRowIndex = this.state.firstRowIndex + this.state.rows - 1;
        const lastColumnIndex = this.state.firstColumnIndex + this.state.cols - 1;
        for (let row = this.state.firstRowIndex; row <= lastRowIndex; row++) {
            for (let col = this.state.firstColumnIndex; col <= lastColumnIndex; col++) {
                positions.push({ row, col });
            }
        }
        return positions;
    }

    private triggerPropTurnStartInOrder(): void {
        const orderedItems = [
            ...this.readAliveItems(this.state.recipes),
            ...this.readAliveItems(this.state.consumables),
            ...this.getBoardRealItems(),
        ];
        this.mechanicsEngine.beginActionBatch();
        try {
            for (const item of uniqueLiveItems(orderedItems)) {
                if (!item.alive) continue;
                this.produceBaseMagicBeforeTrigger(item);
                this.mechanicsEngine.triggerPropTurnStart(item, {
                    type: 'roundStarted',
                    item,
                    commissionIndex: this.state.commissionIndex,
                    roundIndex: this.state.roundIndex,
                    totalRoundIndex: this.state.totalRoundIndex,
                    reason: 'propTurnStart',
                });
            }
        } finally {
            this.mechanicsEngine.endActionBatch();
        }
    }

    private readAliveItems(ids: string[]): RuntimePropInstance[] {
        return ids
            .map((id) => this.state.items.find((item) => item.id === id))
            .filter((item): item is RuntimePropInstance => Boolean(item?.alive));
    }

    private produceBaseMagicBeforeTrigger(item: RuntimePropInstance): void {
        if (!item.alive || isEmptyPropInstance(item)) return;
        const baseYield = getRuntimeYield(item);
        const boost = this.state.roundYieldBoosts[item.id];
        const baseMagic = boost
            ? baseYield * boost.multiplier + boost.add
            : baseYield;
        if (baseMagic === 0) return;
        this.addMagic(baseMagic, item, [item]);
    }

    private getBoardRealItems(): RuntimePropInstance[] {
        return this.mechanicsEngine.getAllBoardItems(false);
    }

    private clearRoundTemporaryState(): void {
        this.eventBus.clearScope('round');
        this.mechanicsEngine.clearRoundYieldBoosts();
        this.mechanicsEngine.clearPurifiedFlags();
        this.mechanicsEngine.clearProtectedFlags();
    }

    private addMagic(delta: number, source: RuntimePropInstance | undefined, targets: RuntimePropInstance[]): void {
        const normalizedDelta = normalizeMagicDelta(delta);
        if (normalizedDelta === 0) return;
        this.state.currentRoundMagic += normalizedDelta;
        this.state.totalMagic += normalizedDelta;
        this.emit({
            type: 'magicChanged',
            item: source,
            source,
            magicDelta: normalizedDelta,
            targetIds: targets.map((item) => item.id),
            reason: 'magicChanged',
        });
    }

    private removeFromOwnerLists(id: string): void {
        removeId(this.state.materialLibrary, id);
        removeId(this.state.recipes, id);
        removeId(this.state.consumables, id);
    }

    private emit(event: PropRuntimeEvent): void {
        this.eventBus.emit(event);
    }
}

function removeId(list: string[], id: string): void {
    const index = list.indexOf(id);
    if (index >= 0) list.splice(index, 1);
}

function cloneJson<T>(value: T): T {
    if (value === undefined || value === null) return value;
    return JSON.parse(JSON.stringify(value)) as T;
}
